/**
 * SOLID Review
 * S — Good. Two concrete transports (GraphQL over Apollo, REST over fetch) plus their Null objects.
 *     One concern — crossing the wire — expressed twice because there are two protocols.
 * O — Good. A new domain reuses these; a new protocol is a third pair.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Each class implements exactly one narrow port.
 * D — Good. Depends on the port interfaces and the Apollo client instance it is CONSTRUCTED with,
 *     not on a module-level singleton.
 */
/**
 * DDD Layer: Infrastructure — the concrete client half of the client↔server seam.
 *
 * ONE INSTANCE PER BOUNDED CONTEXT, and the honest accounting of what that buys: all six GraphQL
 * clients wrap the same Apollo client, so this adds no substitutability — you cannot swap the room
 * domain's transport without swapping the round domain's. It adds ENUMERABILITY, and it makes the
 * seam drawable and countable. `FrameworkConnectorAssembly.ts` made exactly this admission about
 * its five lollipops supplying one ComponentApplication ("This adds no substitutability. It adds
 * enumerability"), and the same sentence is true here.
 *
 * What it DOES buy beyond drawing: a domain's adapter can only execute through the socket its
 * domain owns, so deleting a socket breaks that domain and nothing else — which is what makes the
 * eight seams independently meaningful even though the transport underneath is shared.
 */
import type {
  IPortGraphQLDomainClient, IPortRestApiClient, IPortBeaconClient, IEntityGraphQLAnswer, IConfigHandlerGraphQLStream, TApolloOperations,
  TGraphQLRequest, TGraphQLSubscription,
} from './ports';
import { ok, fail, type Result, type IEntityFailure, type IEntityPortError } from '../../shared/Result';
import { SERVER_REFUSED } from '../../application/ports/contracts';

/**
 * NULL GraphQL client — the frontend's degradation rule (rev4 E.1).
 *
 * Every frontend socket is RequiredPortWithDefault, never RequiredPort, because a UI that throws is
 * a white screen. Reads resolve to nothing and subscriptions never fire, which renders an empty but
 * live interface. `assertAssembled` still fails the BOOT on any unconnected port not on the
 * allowlist, so this is degradation of last resort rather than a way for a missing wire to pass.
 *
 * It implements the port honestly since frontend-infrastructure-oo P3 (§28.288): the port answers OUR `{ data, error }`, which
 * an object that does nothing can give, where Apollo's own result types — the port until then — could only be cast into.
 */
export class NullGraphQLDomainClient implements IPortGraphQLDomainClient {
  async request<TData>(_operation: TGraphQLRequest<TData>): Promise<IEntityGraphQLAnswer<TData>> {
    return { data: null };
  }

  subscribe<TData>(_operation: TGraphQLSubscription<TData>, _handlers: IConfigHandlerGraphQLStream<TData>): () => void {
    return () => undefined;
  }
}

/**
 * THE GRAPHQL CLIENT (frontend-infrastructure-oo P3, 2026-10-29, §28.288): the app's Apollo client, as the six GraphQL sockets
 * carry it. It runs the operation an adapter writes against Apollo's client, and hands a subscription its handlers.
 */
export class ApolloDomainClient implements IPortGraphQLDomainClient {
  constructor(private readonly apollo: TApolloOperations) {}

  request<TData>(operation: TGraphQLRequest<TData>): Promise<IEntityGraphQLAnswer<TData>> {
    return operation(this.apollo);
  }

  subscribe<TData>(operation: TGraphQLSubscription<TData>, handlers: IConfigHandlerGraphQLStream<TData>): () => void {
    const subscription = operation(this.apollo).subscribe(handlers);
    return () => subscription.unsubscribe();
  }
}

/**
 * A refusal the server ANSWERED: its own words, and the HTTP status they came with (frontend strict-ports
 * plan, FE3). An `Error`, so every caller that shows `err.message` still does; the status is what lets the
 * auth gateway tell "there is no player behind this token" (401, 404) from "the server could not be asked"
 * — a fetch that failed throws the browser's own error, never this.
 *
 * It also says how a REST call's answer becomes a port's result (`resultOf`, `failureOf`) — the rule was
 * `restCall` and `restFailure`, module functions, until frontend-infrastructure-oo P3 (§28.288).
 */
export class RestApiError extends Error {
  /** Anything but a refusal the server answered: a diagnostic, not words for a player. */
  private static readonly UNREACHABLE: IEntityPortError = { code: 'UNAVAILABLE', message: 'Could not reach the server.' };

  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'RestApiError';
    this.status = status;
  }

  /**
   * A REST call through a port (frontend strict-ports plan, FE3; shared since FE4a). A refusal the server
   * ANSWERED (`RestApiError`) fails as `SERVER` with its words, fit to show a player; anything else — a fetch
   * that failed, a body the contract refused — is `UNAVAILABLE`, a diagnostic. The REST adapters throw; a
   * port answers.
   */
  static async resultOf<T>(call: () => Promise<T>): Promise<Result<T>> {
    try {
      return ok(await call());
    } catch (error) {
      return RestApiError.failureOf(error);
    }
  }

  /** What a thrown REST call answers at a port, for a gateway that branches on the error first. */
  static failureOf(error: unknown): IEntityFailure {
    if (error instanceof RestApiError) {
      const refused: IEntityPortError = { code: SERVER_REFUSED, message: error.message };
      return fail(refused);
    }
    return fail(RestApiError.UNREACHABLE);
  }
}

/** The browser's `fetch`, narrowed to what the REST client reads of an answer — injectable, so a test needs no browser. */
type TRestFetch = (url: string, init: RequestInit) => Promise<Pick<Response, 'ok' | 'status' | 'json'>>;

/** REST transport for one client-facing surface, over the `fetch` it is handed (the global one until frontend-infrastructure-oo P4). */
export class RestApiClient implements IPortRestApiClient {
  constructor(private readonly baseUrl: string, private readonly fetch: TRestFetch) {}

  async request<T>(
    path: string,
    init: { method: string; body?: unknown; jwt?: string | null },
    parse: (data: unknown) => T,
  ): Promise<T> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (init.jwt) headers['Authorization'] = `Bearer ${init.jwt}`;

    const sent: RequestInit = {
      method: init.method,
      headers,
      ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
    };
    const res = await this.fetch(`${this.baseUrl}${path}`, sent);

    // 204 has no body by contract (DELETE /api/legal/me), so do not ask fetch to parse one.
    //
    // `.catch(() => undefined)` added 2026-08-30, when LegalApiAdapter moved onto this client and
    // its suite caught the difference: a 503 with a non-JSON body (an nginx error page, say) threw
    // the JSON parse error and LOST the status, so the user saw "not json" instead of
    // "Request failed: 503". The status is the useful part of that message.
    const data: unknown = res.status === 204 ? undefined : await res.json().catch(() => undefined);

    if (!res.ok) {
      const err = data as { error?: string } | undefined;
      throw new RestApiError(err?.error ?? `Request failed: ${res.status}`, res.status);
    }
    // PARSED against the contract, not cast. The caller passes the schema's parse function, so a
    // server that stops sending a declared field is an error here rather than an undefined carried
    // into the store.
    return parse(data);
  }
}

/** NULL REST client — same degradation rule as above. */
export class NullRestApiClient implements IPortRestApiClient {
  async request<T>(_path: string, _init: unknown, _parse: (data: unknown) => T): Promise<T> {
    throw new Error('RestApiClient is not connected');
  }
}

/** The browser's two ways out, narrowed to what the beacon uses — injectable, so a test needs no browser. */
export interface IConfigDepBeaconClient {
  readonly sendBeacon: ((url: string, data: Blob) => boolean) | null;
  readonly fetch: (url: string, init: RequestInit) => Promise<unknown>;
}

/**
 * `navigator.sendBeacon` with a `text/plain` body — a CORS-safelisted type, so no preflight — and a
 * `keepalive` fetch when the browser has no beacon or refuses this one (some in-app browsers do). It
 * survives the page unloading, which is when a deploy's chunk errors happen. Never throws.
 */
export class BeaconClient implements IPortBeaconClient {
  constructor(private readonly baseUrl: string, private readonly browser: IConfigDepBeaconClient) {}

  send(path: string, body: string): boolean {
    const url = `${this.baseUrl}${path}`;
    try {
      if (this.browser.sendBeacon !== null && this.browser.sendBeacon(url, new Blob([body], { type: 'text/plain;charset=UTF-8' }))) return true;
      const init: RequestInit = { method: 'POST', body, keepalive: true, headers: { 'Content-Type': 'text/plain;charset=UTF-8' } };
      void this.browser.fetch(url, init).catch(() => undefined);
      return true;
    } catch {
      return false;
    }
  }
}

/** Unwired: nothing leaves the page. */
export class NullBeaconClient implements IPortBeaconClient {
  send(_path: string, _body: string): boolean {
    return false;
  }
}
